<template>
    <v-row justify="center">
        <!-- Product Category Dialog -->
        <ProductCategoryEditDialog
            :dialog.sync="productCategoryDialog"
            v-on:item-created="productCategoryAdded"
        ></ProductCategoryEditDialog>

        <!-- Main dialog -->
        <v-dialog v-model="dialog" persistent max-width="800px">
            <v-card>
                <v-alert class="mb-0" v-if="error" tile type="error">
                    <span>{{ error }}</span>
                </v-alert>

                <v-card-title class="primary white--text">{{
                    formTitle
                }}</v-card-title>

                <v-card-text class="mt-4">
                    <v-form ref="form">
                        <v-row align="center">
                            <!-- Product Category -->
                            <v-col cols="12">
                                <v-autocomplete
                                    v-model="editedItem.productCategoryNodeId"
                                    :items="productCategories"
                                    item-text="node.name"
                                    item-value="node.id"
                                    :loading="isLoadingProductCategories"
                                    :rules="[]"
                                    label="Product Category"
                                    class="subtitle-2"
                                    outlined
                                >
                                    <template
                                        v-if="
                                            hasPermission('add_productcategory')
                                        "
                                        v-slot:append-outer
                                    >
                                        <v-btn text small tabindex="-1">
                                            <v-icon
                                                color="grey"
                                                @click="addProductCategory"
                                                tabindex="-1"
                                                >add</v-icon
                                            >
                                        </v-btn>
                                    </template>
                                </v-autocomplete>
                            </v-col>

                            <!-- Product Name -->
                            <v-col cols="12">
                                <v-text-field
                                    v-model="editedItem.name"
                                    :counter="100"
                                    :rules="[
                                        rules.required,
                                        rules.maxLength(100)
                                    ]"
                                    label="Name*"
                                    outlined
                                    class="subtitle-2"
                                ></v-text-field>
                            </v-col>

                            <!-- External Code -->
                            <v-col cols="12" md="6">
                                <v-text-field
                                    v-model="editedItem.externalCode"
                                    :counter="50"
                                    :rules="[rules.maxLength(50)]"
                                    label="External Code"
                                    outlined
                                    class="subtitle-2"
                                ></v-text-field>
                            </v-col>

                            <!-- External Id -->
                            <v-col cols="12" md="6">
                                <v-text-field
                                    v-model="editedItem.externalId"
                                    :counter="50"
                                    :rules="[rules.maxLength(50)]"
                                    label="External ID"
                                    outlined
                                    class="subtitle-2"
                                ></v-text-field>
                            </v-col>

                            <!-- Is Dynamic -->
                            <v-col cols="12" class="py-0">
                                <v-row dense>
                                    <v-col cols="6">
                                        <v-switch
                                            v-model="editedItem.isDynamic"
                                            label="Is Dynamic"
                                            color="primary"
                                        ></v-switch>
                                    </v-col>
                                    <v-col cols="3" v-if="editedItem.isDynamic">
                                        <v-text-field
                                            v-model="
                                                editedItem.numMixPlantsPerUnitMin
                                            "
                                            type="number"
                                            label="Min #Plants*"
                                            min="1"
                                            outlined
                                            :rules="[rules.required]"
                                            class="subtitle-2"
                                        ></v-text-field>
                                    </v-col>
                                    <v-col cols="3" v-if="editedItem.isDynamic">
                                        <v-text-field
                                            v-model="
                                                editedItem.numMixPlantsPerUnitMax
                                            "
                                            type="number"
                                            label="Max #Plants*"
                                            min="1"
                                            outlined
                                            :rules="[rules.required]"
                                            class="subtitle-2"
                                        ></v-text-field>
                                    </v-col>
                                </v-row>
                            </v-col>

                            <!-- Color Range -->
                            <v-col cols="12" class="py-0">
                                <v-row
                                    v-if="editedItem.isDynamic"
                                    dense
                                    align-start
                                    justify-start
                                >
                                    <v-col cols="6">
                                        <v-switch
                                            v-model="
                                                editedItem.numColorsCheckbox
                                            "
                                            label="# Colors Per Unit"
                                            color="primary"
                                        ></v-switch>
                                    </v-col>
                                    <v-col
                                        cols="3"
                                        v-if="editedItem.numColorsCheckbox"
                                    >
                                        <v-text-field
                                            v-model="
                                                editedItem.numColorsPerUnitMin
                                            "
                                            type="number"
                                            label="Min #Colors*"
                                            min="1"
                                            outlined
                                            :rules="[rules.required]"
                                            class="subtitle-2"
                                        ></v-text-field>
                                    </v-col>
                                    <v-col
                                        cols="3"
                                        v-if="editedItem.numColorsCheckbox"
                                    >
                                        <v-text-field
                                            v-model="
                                                editedItem.numColorsPerUnitMax
                                            "
                                            type="number"
                                            label="Max #Colors*"
                                            min="1"
                                            outlined
                                            :rules="[rules.required]"
                                            class="subtitle-2"
                                        ></v-text-field>
                                    </v-col>
                                    <v-col v-if="!editedItem.numColorsCheckbox">
                                        <div class="headline grey--text mt-3">
                                            No limit
                                        </div>
                                    </v-col>
                                </v-row>
                            </v-col>

                            <!-- Print Priority -->
                            <v-col cols="6">
                                <v-text-field
                                    v-model="editedItem.printPriority"
                                    type="number"
                                    label="Print Priority"
                                    outlined
                                    hide-details
                                    class="subtitle-2"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="6">
                                <v-text-field
                                    v-model="editedItem.unitCorrection"
                                    type="number"
                                    label="Unit Correction"
                                    outlined
                                    hide-details
                                    class="subtitle-2"
                                ></v-text-field>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>
                <v-card-actions class="pb-5 pr-5">
                    <v-spacer></v-spacer>
                    <v-btn text rounded @click="close">Cancel</v-btn>
                    <v-btn
                        outlined
                        rounded
                        :loading="isSaving"
                        color="primary"
                        @click="save"
                        >Save</v-btn
                    >
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-row>
</template>

<script>
import helper from "@/utils/helper.js";
import ProductCategoryEditDialog from "@/components/products/ProductCategoryEditDialog.vue";
// import Config from "@/utils/config.json";

export default {
    name: "product-edit-dialog",
    props: ["dialog", "object"],
    components: {
        ProductCategoryEditDialog
    },
    data() {
        return {
            error: null,
            isLoadingProductCategories: false,
            isSaving: false,
            currencies: ["EUR", "USD"],
            editedItem: {
                id: "",
                name: "",
                externalId: null,
                externalCode: null,
                productCategoryNodeId: "",
                isDynamic: false,
                numMixPlantsPerUnitMin: null,
                numMixPlantsPerUnitMax: null,
                numColorsPerUnitMin: null,
                numColorsPerUnitMax: null,
                numColorsCheckbox: false,
                printPriority: null,
                unitCorrection: null
            },
            defaultItem: {
                id: "",
                name: "",
                externalId: null,
                externalCode: null,
                productCategoryNodeId: "",
                isDynamic: false,
                numMixPlantsPerUnitMin: null,
                numMixPlantsPerUnitMax: null,
                numColorsPerUnitMin: null,
                numColorsPerUnitMax: null,
                numColorsCheckbox: false,
                printPriority: null,
                unitCorrection: null
            },
            rules: {
                required: v => !helper.isEmpty(v) || "This field is required",
                minLength: len => v =>
                    (v || "").length >= len ||
                    `Invalid character length, required ${len}`,
                maxLength: len => v => (v || "").length <= len || "Too long"
                // nameRules: [
                //     v => !!v || "Name is required",
                //     v =>
                //         v.length <= 50 || "Name must be less than 50 characters"
                // ]
            },
            productCategoryDialog: false
        };
    },
    computed: {
        productCategories: function() {
            return this.$store.state.productCategories.productCategories;
        },
        formTitle() {
            return this.object ? "Edit Product" : "New Product";
        }
    },
    watch: {
        // reset form when dialog open or close
        dialog(val) {
            this.resetForm();

            // if open dialog, initialize selections
            if (val) {
                this.initializeSelections();
            }
        }
    },
    created() {
        this.hasPermission = helper.hasPermission;
    },
    methods: {
        initializeSelections() {
            // fetch greenhouse departments
            this.isLoadingProductCategories = true;
            this.$store
                .dispatch("productCategories/fetchProductCategories")
                .then(() => {})
                .catch(error => {
                    /*handle errors*/
                    console.log(error);
                })
                .finally(() => (this.isLoadingProductCategories = false));
        },

        resetForm() {
            // reset form state
            this.error = null;
            this.isSaving = false;
            if (this.$refs.form) {
                this.$refs.form.resetValidation();
            }

            // when editing, set form with object
            if (this.dialog && this.object) {
                this.editedItem.id = this.object.id;
                this.editedItem.name = this.object.name;
                this.editedItem.externalId = this.object.externalId;
                this.editedItem.externalCode = this.object.externalCode;
                this.editedItem.isDynamic = this.object.isDynamic;
                this.editedItem.numMixPlantsPerUnitMin = this.object.numMixPlantsPerUnitMin;
                this.editedItem.numMixPlantsPerUnitMax = this.object.numMixPlantsPerUnitMax;
                this.editedItem.numColorsCheckbox =
                    this.object.numColorsPerUnitMin ||
                    this.object.numColorsPerUnitMax;
                this.editedItem.numColorsPerUnitMin = this.object.numColorsPerUnitMin;
                this.editedItem.numColorsPerUnitMax = this.object.numColorsPerUnitMax;
                this.editedItem.printPriority = this.object.printPriority;
                this.editedItem.unitCorrection = this.object.unitCorrection;
                this.editedItem.productCategoryNodeId = this.object
                    .productCategory
                    ? this.object.productCategory.id
                    : null;
                // this.editedItem.salesPrice = this.object.salesPrice;
                // this.editedItem.currency = this.object.currency;
            }
            // when new or closing, set form with default
            else {
                this.editedItem = Object.assign({}, this.defaultItem);
            }
        },

        close() {
            this.$emit("update:dialog", false);
            this.resetForm();
        },

        save() {
            // check if form valid
            if (!this.$refs.form.validate()) {
                return;
            }

            // set form state
            this.error = null;
            this.isSaving = true;

            // prepare api call payload
            var payload = {
                // sourceSystemNodeId: Config.sourceSystemNodeId,
                name: this.editedItem.name,
                externalId: this.editedItem.externalId,
                externalCode: this.editedItem.externalCode,
                productCategoryNodeId: this.editedItem.productCategoryNodeId,
                isDynamic: this.editedItem.isDynamic,
                numMixPlantsPerUnitMin: this.editedItem.isDynamic
                    ? this.editedItem.numMixPlantsPerUnitMin
                    : null,
                numMixPlantsPerUnitMax: this.editedItem.isDynamic
                    ? this.editedItem.numMixPlantsPerUnitMax
                    : null,
                numColorsPerUnitMin:
                    !this.editedItem.numColorsCheckbox ||
                    this.editedItem.numColorsPerUnitMin === ""
                        ? null
                        : this.editedItem.numColorsPerUnitMin,
                numColorsPerUnitMax:
                    !this.editedItem.numColorsCheckbox ||
                    this.editedItem.numColorsPerUnitMax === ""
                        ? null
                        : this.editedItem.numColorsPerUnitMax
            };

            if (this.editedItem.printPriority) {
                payload = {
                    ...payload,
                    printPriority: this.editedItem.printPriority
                };
            }

            if (this.editedItem.unitCorrection) {
                payload = {
                    ...payload,
                    unitCorrection: this.editedItem.unitCorrection
                };
            }

            // if editing
            if (this.object) {
                // attach object id
                payload.id = this.editedItem.id;

                // call update api
                this.$store
                    .dispatch("products/updateProduct", payload)
                    .then(response => {
                        // update parent object
                        this.$emit("update:object", response);
                        this.close();
                    })
                    .catch(error => {
                        this.error = error.graphQLErrors[0].message;
                        this.isSaving = false;
                    })
                    .finally(() => {});
            }
            // if new
            else {
                // create product via store action
                this.$store
                    .dispatch("products/createProduct", payload)
                    .then(response => {
                        this.$emit("item-created", response);
                        this.close();
                    })
                    .catch(error => {
                        this.error = error.graphQLErrors
                            .map(error => error.message)
                            .join(", ");
                        this.isSaving = false;
                    })
                    .finally(() => {});
            }
        },

        addProductCategory() {
            this.productCategoryDialog = true;
        },
        productCategoryAdded(item) {
            this.editedItem.productCategoryNodeId = item.id;
        }
    }
};
</script>